import { FC, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { Gantt } from '../../../../../_metronic/partials/gantt/src'
import { ViewMode } from '../../../../../_metronic/partials/gantt/src/types/public-types'
import '../../../../../_metronic/partials/gantt/src/index.css'
import './PublicClient.css'

type Phase = {
  id: string
  schedule_id?: number
  name: string
  start: Date
  end: Date
  type: 'task' | 'milestone' | 'project'
  progress: number
  order?: number
}

type Schedule = {
  id?: string
  project_id?: number
  phases: Array<{
    id: string
    schedule_id?: number
    name?: string
    begin_date?: string
    end_date?: string
    order?: number
  }>
  url?: string
}

type Props = {
  clientUrl: string
  isAuthenticated?: boolean
  token?: string | null
}

const PublicSchedule: FC<Props> = ({ clientUrl, isAuthenticated = false, token = null }) => {
  const intl = useIntl()
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [phases, setPhases] = useState<Phase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
        
        // Use protected endpoint if authenticated, otherwise use public endpoint
        const baseUrl = isAuthenticated 
          ? `${apiUrl}/api/client-portal/project/${clientUrl}`
          : `${apiUrl}/api/public/client/${clientUrl}`
        
        const headers: Record<string, string> = {}
        if (isAuthenticated && token) {
          headers['Authorization'] = `Bearer ${token}`
          headers['Content-Type'] = 'application/json'
        }
        
        const response = await axios.get(`${baseUrl}/schedule`, { headers })
        
        // Handle different response formats
        const responseData = response.data?.data || response.data
        
        if (responseData && responseData.length > 0) {
          setSchedules(responseData)
          
          // Converter fases para o formato do Gantt
          const tempPhases: Phase[] = []
          responseData[0].phases?.forEach((phase: any) => {
            tempPhases.push({
              id: phase.id?.toString() || '',
              name: phase.name || '',
              start: new Date(phase.begin_date || ''),
              end: new Date(phase.end_date || ''),
              type: 'task',
              progress: 100,
              order: phase.order
            })
          })
          setPhases(tempPhases)
        }
      } catch (err: any) {
        console.error('Erro ao carregar cronograma:', err)
        setError('Erro ao carregar cronograma do projeto')
      } finally {
        setLoading(false)
      }
    }

    if (clientUrl) {
      fetchSchedule()
    }
  }, [clientUrl])

  if (loading) {
    return (
      <div className='pc-state'>
        <div className='spinner-border pc-spinner' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='pc-state' role='alert'>
        <i className='bi bi-exclamation-triangle' aria-hidden='true'></i>
        <p className='pc-state-title'>Não foi possível carregar o cronograma</p>
        <p className='mb-0'>{error}</p>
      </div>
    )
  }

  if (!schedules.length || !phases.length) {
    return null // Não renderiza nada se não há cronograma
  }

  return (
    <section className='pc-section'>
      <div className='pc-section-header'>
        <h2 className='pc-section-title'>
          {intl.formatMessage({ id: 'PROJECT.LABEL.PROJECT_SCHEDULE' })}
        </h2>
      </div>

      <div className='pc-gantt gantt-wrapper'>
        <Gantt
          tasks={phases}
          viewMode={ViewMode.Day}
          listCellWidth='250px'
          locale='pt-BR'
          barBackgroundColor='#dbe8a6'
          barBackgroundSelectedColor='#c9dc86'
          barProgressColor='#a3c23f'
          barProgressSelectedColor='#8fae33'
          rowHeight={45}
          barCornerRadius={5}
          columnWidth={70}
        />
      </div>
    </section>
  )
}

export { PublicSchedule }
